import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  noIndex?: boolean;
  structuredData?: object;
}

const SITE_NAME = 'Living Tribute';
const DEFAULT_DESCRIPTION = 'Create beautiful digital memorials and tribute videos. Transform cherished photos into living memories that honor and celebrate the lives of those you love.';
const BASE_URL = 'https://livingtribute.co';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  noIndex = false,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Digital Memorials & Tribute Videos`;
  const fullCanonicalUrl = canonicalUrl ? `${BASE_URL}${canonicalUrl}` : undefined;
  
  // Ensure ogImage is an absolute URL for social sharing
  const resolvedOgImage = (() => {
    if (!ogImage) return DEFAULT_OG_IMAGE;
    // If already absolute URL, use as-is
    if (ogImage.startsWith('http://') || ogImage.startsWith('https://')) {
      return ogImage;
    }
    // If relative URL, make absolute
    return `${BASE_URL}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;
  })();

  // Default Organization structured data
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Living Tribute',
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    description: DEFAULT_DESCRIPTION,
    sameAs: [
      'https://facebook.com/livingtribute',
      'https://instagram.com/livingtribute',
      'https://twitter.com/livingtribute',
    ],
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content="Living Tribute" />
      
      {/* Canonical URL */}
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@livingtribute" />
      <meta name="twitter:creator" content="@livingtribute" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedOgImage} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
}

// Pre-configured SEO for specific page types
export function HomeSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Living Tribute',
    url: 'https://livingtribute.co',
    description: 'Create beautiful digital memorials and tribute videos that honor and celebrate the lives of those you love.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://livingtribute.co/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <SEO
      title="Create Beautiful Digital Memorials & Tribute Videos"
      description="Transform cherished photos into living memories. Create stunning tribute videos and online memorials to honor and celebrate the lives of those you love."
      canonicalUrl="/"
      structuredData={structuredData}
    />
  );
}

export function PricingSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Pricing - Living Tribute',
    description: 'Simple, transparent pricing for digital memorials and tribute videos. Start free, upgrade when ready.',
    url: 'https://livingtribute.co/pricing',
  };

  return (
    <SEO
      title="Pricing"
      description="Simple, transparent pricing for digital memorials and tribute videos. Create a free tribute or upgrade for premium features like animated photos and tribute videos."
      canonicalUrl="/pricing"
      structuredData={structuredData}
    />
  );
}

export function AboutSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Living Tribute',
    description: 'Learn about our mission to help families honor and celebrate the lives of those they love through meaningful digital memorials.',
    url: 'https://livingtribute.co/about',
  };

  return (
    <SEO
      title="About Us"
      description="Living Tribute helps families honor loved ones through beautiful digital memorials. Learn about our mission, values, and commitment to compassionate technology."
      canonicalUrl="/about"
      structuredData={structuredData}
    />
  );
}

export function ContactSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Living Tribute',
    description: 'Get in touch with our compassionate support team for help with your digital memorial or tribute video.',
    url: 'https://livingtribute.co/contact',
  };

  return (
    <SEO
      title="Contact Us"
      description="Reach out to Living Tribute's compassionate support team. We're here to help with questions about creating digital memorials and tribute videos."
      canonicalUrl="/contact"
      structuredData={structuredData}
    />
  );
}

export function ResourcesSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Resources & Support - Living Tribute',
    description: 'Guides, FAQs, and support resources for creating meaningful digital memorials and tribute videos.',
    url: 'https://livingtribute.co/resources',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is Living Tribute?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Living Tribute is a platform that helps families create beautiful digital memorials and tribute videos to honor and celebrate the lives of loved ones.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I create a tribute?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Creating a tribute is simple: sign up, add photos and memories, and optionally create animated photos or tribute videos. You can invite family to collaborate.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is my tribute private?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, all tributes are private by default. You control who can view your tribute through privacy settings and optional password protection.',
          },
        },
      ],
    },
  };

  return (
    <SEO
      title="Resources & Support"
      description="Everything you need to create meaningful tributes. Step-by-step guides, obituary writing tips, FAQs, and compassionate support for families."
      canonicalUrl="/resources"
      structuredData={structuredData}
    />
  );
}

export function PrivacySEO() {
  return (
    <SEO
      title="Privacy Policy"
      description="Learn how Living Tribute protects your privacy and personal information. We're committed to keeping your memories safe and secure."
      canonicalUrl="/privacy"
    />
  );
}

export function WhyLivingTributeSEO() {
  return (
    <SEO
      title="Why Choose Living Tribute"
      description="Discover what makes Living Tribute different: compassionate design, collaborative features, AI-powered assistance, and beautiful memorial videos."
      canonicalUrl="/why-living-tribute"
    />
  );
}

export function SupportCenterSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Living Tribute Support Center',
    description: 'Find answers, guidance, and step-by-step help for creating digital memorials and tribute videos.',
    url: 'https://livingtribute.co/support',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How do I create a tribute?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Click "Start a Tribute", enter the name of the person you\'re honoring, add details, and personalize with photos, obituary, and more.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I invite family members to contribute?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Open your tribute, go to the Sharing section, click Invite Collaborators, and enter their email addresses with chosen permissions.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I create a tribute video?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Upload at least 5 photos, go to the Tribute Video section, select photos, choose music, and click Generate Video.',
          },
        },
      ],
    },
  };

  return (
    <SEO
      title="Support Center"
      description="Find answers, guidance, and step-by-step help for creating digital memorials. AI-powered assistance and self-service support for Living Tribute users."
      canonicalUrl="/support"
      structuredData={structuredData}
    />
  );
}

export function PetsSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Pet Tributes - Living Tribute',
    description: 'Create beautiful memorial tributes for beloved pets. Honor your companion with photos, memories, and tribute videos.',
    url: 'https://livingtribute.co/pets',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Can I create a tribute for my pet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes! Living Tribute offers a special Pet Mode with prompts and language designed specifically for honoring beloved pets of all kinds.',
          },
        },
        {
          '@type': 'Question',
          name: 'What animals can I create a tribute for?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You can create a tribute for any beloved pet — dogs, cats, horses, birds, rabbits, guinea pigs, fish, or any companion animal.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do I need to know my pet\'s exact birth date?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No, for pet tributes we only ask for month and year since many pet owners don\'t know the exact date. You can also leave it blank if unknown.',
          },
        },
      ],
    },
  };

  return (
    <SEO
      title="Pet Tributes - Honor Your Beloved Companion"
      description="Create a beautiful memorial for your pet with photos, memories, and a touching tribute video. Because they were family. Dogs, cats, horses, and all beloved companions."
      canonicalUrl="/pets"
      structuredData={structuredData}
    />
  );
}

export function TributeVideoSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Create a Tribute Video',
    description: 'Create a beautiful tribute video to honor and celebrate the life of someone you love.',
    url: 'https://livingtribute.co/tribute-video',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Upload Photos',
        text: 'Upload your favorite photos from your device, cloud storage, or social media.'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Choose Music',
        text: 'Select from our curated library or upload your own meaningful track.'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Generate Video',
        text: 'Our AI arranges your photos with smooth transitions and timing.'
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Share',
        text: 'Download or share privately with family and friends.'
      }
    ]
  };

  return (
    <SEO
      title="Create a Tribute Video"
      description="Transform cherished photos into a beautiful tribute video. Honor your loved one with music, smooth transitions, and heartfelt memories. No technical skills required."
      canonicalUrl="/tribute-video"
      structuredData={structuredData}
    />
  );
}

export function DigitalMemorialSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Digital Memorial for a Loved One',
    description: 'Create a lasting online memorial that brings together photos, stories, and memories in one meaningful place.',
    url: 'https://livingtribute.co/digital-memorial',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a digital memorial?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A digital memorial is an online space dedicated to honoring and remembering someone who has passed. It can include photos, stories, an obituary, a eulogy, and tribute videos.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is the memorial private?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, by default. You control who can view your memorial through privacy settings, secure links, or password protection.'
          }
        }
      ]
    }
  };

  return (
    <SEO
      title="Digital Memorial for a Loved One"
      description="Create a beautiful, lasting digital memorial. Gather photos, stories, and memories in one meaningful place to honor and celebrate the life of someone you love."
      canonicalUrl="/digital-memorial"
      structuredData={structuredData}
    />
  );
}

export function CelebrationOfLifeVideoSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Celebration of Life Video',
    description: 'Create a beautiful video that celebrates the joy, love, and unique spirit of someone you cherish.',
    url: 'https://livingtribute.co/celebration-of-life-video',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Can I play this at a memorial service?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. Our videos are designed to be suitable for memorial services, celebrations of life, funeral gatherings, or private family viewings.'
          }
        },
        {
          '@type': 'Question',
          name: 'How many photos should I include?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We recommend 15-75 photos for most celebration of life videos. This gives enough variety to tell their story without making the video too long.'
          }
        }
      ]
    }
  };

  return (
    <SEO
      title="Celebration of Life Video"
      description="Create a heartfelt celebration of life video. Transform photos into a beautiful tribute that honors joy, love, and cherished memories. Perfect for memorial services."
      canonicalUrl="/celebration-of-life-video"
      structuredData={structuredData}
    />
  );
}

export function OnlineMemorialPageSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Online Memorial Page',
    description: 'Create a beautiful, lasting online memorial page where family and friends can share memories and celebrate a life.',
    url: 'https://livingtribute.co/online-memorial-page',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is an online memorial page?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'An online memorial page is a dedicated website created to honor and remember someone who has passed. It serves as a central place to share photos, stories, obituaries, and condolences.'
          }
        },
        {
          '@type': 'Question',
          name: 'Do visitors need to create an account?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Anyone you share the link with can view the memorial page without creating an account.'
          }
        }
      ]
    }
  };

  return (
    <SEO
      title="Online Memorial Page"
      description="Create a lasting online memorial page. Share photos, memories, and tributes with family and friends in one beautiful, permanent place."
      canonicalUrl="/online-memorial-page"
      structuredData={structuredData}
    />
  );
}

export function HowToMakeTributeVideoSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Make a Tribute Video',
    description: 'A step-by-step guide to creating a beautiful tribute video honoring someone you love.',
    url: 'https://livingtribute.co/how-to-make-a-tribute-video',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Gather and Upload Photos',
        text: 'Collect meaningful photos and upload them from your phone, computer, or cloud storage. Aim for 15-75 photos.'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Enhance Photos (Optional)',
        text: 'Use AI to enhance older photos, add gentle motion effects, or restore damaged images.'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Choose Background Music',
        text: 'Select from our curated library or upload your own meaningful song.'
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Generate Your Video',
        text: 'Click Generate and our AI creates a flowing visual story with smooth transitions.'
      },
      {
        '@type': 'HowToStep',
        position: 5,
        name: 'Download and Share',
        text: 'Download in HD quality, share privately with family, or play at a memorial service.'
      }
    ]
  };

  return (
    <SEO
      title="How to Make a Tribute Video"
      description="Step-by-step guide to creating a beautiful tribute video. Learn how to gather photos, choose music, and generate a heartfelt video honoring someone you love."
      canonicalUrl="/how-to-make-a-tribute-video"
      structuredData={structuredData}
    />
  );
}

export function PhotosToLifeSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Bring Photos to Life with AI Animation',
    description: 'Transform still photos into gentle, living memories with subtle AI-powered animation.',
    url: 'https://livingtribute.co/photos-to-life',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Upload Photos',
        text: 'Add your favorite photos to your tribute from your device, cloud storage, or social media.'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Select Photos to Animate',
        text: 'Choose which photos you want to bring to life with gentle, natural motion.'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Preview and Refine',
        text: 'Watch your photos come alive with subtle animation and adjust as needed.'
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Use in Your Tribute',
        text: 'Include animated photos in your tribute video or memorial page.'
      }
    ],
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What does bringing photos to life mean?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Using advanced AI, we add subtle, natural motion to still photographs such as gentle head movements, soft smiles, or hair moving in a breeze.'
          }
        },
        {
          '@type': 'Question',
          name: 'Will the animation look natural?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Our AI creates subtle, respectful animations that feel natural rather than artificial, prioritizing lifelike movement over dramatic effects.'
          }
        }
      ]
    }
  };

  return (
    <SEO
      title="Bring Photos to Life - AI Photo Animation"
      description="Transform cherished still photos into gentle, living memories. Our AI adds subtle, natural motion to photographs, helping you remember them as they were."
      canonicalUrl="/photos-to-life"
      structuredData={structuredData}
    />
  );
}

export function CollaborateSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Collaborate With Family & Friends on a Memorial Tribute',
    description: 'Invite loved ones to contribute photos, stories, and memories to create a richer celebration of life.',
    url: 'https://livingtribute.co/collaborate',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do collaborators need to create an account?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Collaborators receive a secure link allowing them to contribute without creating an account, making it easy for anyone to participate.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can I control what collaborators can do?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. You have full control over permissions. You can allow someone to only upload photos, or give them ability to edit captions, favorite images, and contribute to the obituary.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can multiple people work on the tribute at the same time?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Living Tribute supports real-time collaboration. Multiple family members can upload photos, write captions, and make edits simultaneously.'
          }
        }
      ]
    }
  };

  return (
    <SEO
      title="Collaborate With Family & Friends - Memorial Tribute"
      description="Create a tribute together. Invite family and friends to contribute photos, stories, and memories, building a richer celebration of a life well lived."
    canonicalUrl="/collaborate"
    structuredData={structuredData}
  />
);
}

export function CraftObituarySEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Write an Obituary',
    description: 'A guided approach to writing a meaningful obituary with AI assistance and collaborative tools.',
    url: 'https://livingtribute.co/craft-obituary',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Answer Simple Questions',
        text: 'Our guided interview walks you through key moments, relationships, and qualities about your loved one.'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Let AI Draft for You',
        text: 'Based on your answers, our AI creates a thoughtful first draft that captures their unique story.'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Personalize and Refine',
        text: 'Edit freely with AI assistance. Adjust tone, add details, or let collaborators contribute.'
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Share or Print',
        text: 'Download a beautifully formatted document for funeral programs, newspapers, or online memorials.'
      }
    ],
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How is this different from writing an obituary myself?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our guided approach eliminates the blank page problem. You answer thoughtful questions about your loved one, and our AI weaves your answers into a cohesive, well-written draft you can personalize.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can family members help write the obituary?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes! You can invite collaborators who can add their own memories, suggest edits, and review the draft together.'
          }
        },
        {
          '@type': 'Question',
          name: 'How long does it take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most people complete a first draft in 15-30 minutes using our guided questions. You can then spend as much or as little time refining it.'
          }
        }
      ]
    }
  };

  return (
    <SEO
      title="Craft a Beautiful Obituary - Guided Writing Tools"
      description="Find the right words to honor their story. Our guided obituary writing tools and AI assistant help you create a meaningful tribute without facing a blank page."
      canonicalUrl="/craft-obituary"
      structuredData={structuredData}
    />
  );
}
